// src\themes\tolica\components\AdminSupportButton.tsx
'use client';

import { useState } from 'react';
import { Headset, X, Phone, MessageCircle } from 'lucide-react';
import { useDrawerStoreTolica } from '@/stores/drawerStore';

interface SupportWidgetProps {
    active?: boolean;
}

// این کامپوننت علاوه بر تولیکا، توسط وندا هم استفاده می‌شه (چون
// NEXT_PUBLIC_THEME=tolica رو داره - هم‌تم با تولیکا). قبلاً chatUrl و
// phoneNumber هاردکد مال تولیکا بودن، یعنی مشتری‌های وندا روی دکمه‌ی
// پشتیبانی که می‌زدن، چت/شماره‌ی تولیکا باز می‌شد - این باگ واقعی رو هم
// همین‌جا رفع کردیم (2026-10-19).
type BrandKey = 'tolica' | 'vanda' | 'shemshak';

const BRAND_CONFIG: Record<BrandKey, {
    color: string;
    colorDark: string;
    chatUrl: string;
    phoneNumber?: string; // اگه نباشه، منوی انتخاب نشون داده نمی‌شه و مستقیم چت باز می‌شه
}> = {
    tolica: {
        color: 'rgb(0,0,0)',
        colorDark: 'rgb(38,38,38)',
        chatUrl: 'https://club.tolica.ir/fa/club/support?widget=true',
        phoneNumber: '09120337430',
    },
    // TODO: my-club هنوز برای وندا/شمشک deploy نشده (تایید شده با کارفرما
    // 2026-10-19) - این آدرس طبق همون الگوی club.<دامنه‌ی اصلی برند> که
    // تولیکا/چوپر دارن حدس زده شده؛ وقتی my-club دیپلوی شد باید تایید/تصحیح
    // بشه. فعلاً قطعاً از آدرس اشتباه قبلی (چت تولیکا) بهتره.
    vanda: {
        color: 'rgb(28,43,79)', // سرمه‌ای وندا
        colorDark: 'rgb(18,28,52)',
        chatUrl: 'https://club.vanda-home.ir/fa/club/support?widget=true',
    },
    shemshak: {
        color: 'rgb(166,25,46)', // قرمز شمشک
        colorDark: 'rgb(130,18,35)',
        chatUrl: 'https://club.shemshakco.ir/fa/club/support?widget=true',
    },
};

function getBrandConfig() {
    const siteName = process.env.NEXT_PUBLIC_SITE_NAME;
    if (siteName === 'vanda') return BRAND_CONFIG.vanda;
    if (siteName === 'shemshak') return BRAND_CONFIG.shemshak;
    return BRAND_CONFIG.tolica;
}

export default function SupportWidget({ active = true }: SupportWidgetProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    // موبایل سایدبار (Sidebar.tsx) روی همین z-index محدوده‌ی بالایی باز
    // می‌شه و این دکمه رو از روش عبور می‌داد؛ وقتی منو بازه این دکمه رو
    // مخفی می‌کنیم تا روی محتوای منو ننشینه.
    const isMobileDrawerOpen = useDrawerStoreTolica((s) => s.open);

    if (!active || isMobileDrawerOpen) return null;

    const brand = getBrandConfig();
    const hasPhone = Boolean(brand.phoneNumber);

    const handleMainButtonClick = () => {
        if (isOpen) {
            setIsOpen(false);
            return;
        }
        // برندهایی که شماره تماس ندارن (مثل چوپر)، مستقیم چت باز می‌شه - بدون
        // منوی میانی انتخاب
        if (!hasPhone) {
            setIsOpen(true);
            return;
        }
        setShowMenu((prev) => !prev);
    };

    return (
        <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-3">

            {isOpen && (
                <div className="mb-0 w-[360px] h-[550px] sm:h-[600px] bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 transition-all duration-300 origin-bottom-right flex flex-col">
                    <div
                        className="text-white p-4 flex justify-between items-center shadow-md z-10"
                        style={{ backgroundColor: brand.color }}
                    >
                        <div className="flex flex-col">
                            <h3 className="font-bold text-md">پشتیبانی آنلاین</h3>
                            <span className="text-xs opacity-80">پاسخگوی سوالات شما هستیم</span>
                        </div>
                        <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1.5 rounded-lg transition-colors">
                            <X size={20} />
                        </button>
                    </div>

                    <iframe
                        src={brand.chatUrl}
                        className="w-full flex-1 border-none bg-gray-50"
                        title="پشتیبانی"
                    />
                </div>
            )}

            {/* منوی انتخاب راه ارتباطی - فقط وقتی برند شماره تماس داره (تولیکا) */}
            {hasPhone && showMenu && !isOpen && (
                <div className="flex flex-col gap-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-2 min-w-[200px] animate-in fade-in slide-in-from-bottom-2 duration-200">
                    <button
                        onClick={() => {
                            setIsOpen(true);
                            setShowMenu(false);
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-right"
                    >
                        <MessageCircle className="w-5 h-5 shrink-0" style={{ color: brand.color }} />
                        <span className="text-sm font-medium text-gray-800">چت آنلاین</span>
                    </button>

                    <a
                        href={`tel:${brand.phoneNumber}`}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-right"
                    >
                        <Phone className="w-5 h-5 shrink-0" style={{ color: brand.color }} />
                        <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-800">تماس تلفنی</span>
                            <span dir="ltr" className="text-xs text-gray-500">{brand.phoneNumber}</span>
                        </div>
                    </a>
                </div>
            )}

            {!isOpen && (
                <button
                    onClick={handleMainButtonClick}
                    className="flex items-center justify-center w-14 h-14 text-white rounded-full shadow-xl hover:scale-110 hover:shadow-2xl transition-all duration-300"
                    style={{ backgroundColor: brand.color }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = brand.colorDark)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = brand.color)}
                    aria-label="پشتیبانی"
                >
                    {showMenu && hasPhone ? <X className="w-7 h-7" /> : <Headset className="w-7 h-7" />}
                </button>
            )}
        </div>
    );
}
